import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginatedResponse, { PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import UserCoworkerQuery from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import UserCoworkerSchema, { UserCoworkerJSON } from "users/information/coworker-information/_schemas/user-coworker-schema";
import z from "zod";

type UserCoworkerTag = { type: "UserCoworker"; id?: string };
export type UserCoworkerListArgs = {
  query: UserCoworkerQuery;
  append: boolean;
};

export function parseUserCoworkerPaginationHeaders(meta?: { response?: { headers: { get(name: string): string | null } } }): PaginationHeaders {
  const headers = meta?.response?.headers.get("x-pagination") ?? "";
  return PaginatedResponse.parsePaginationHeader(headers);
}

export function getUserCoworkerTags(result?: Paginated<UserCoworkerJSON>): UserCoworkerTag[] {
  return result
    ? [
        ...result.data.map((item) => ({
          type: "UserCoworker" as const,
          id: item.id ?? undefined,
        })),
        { type: "UserCoworker" as const, id: "LIST" },
      ]
    : [{ type: "UserCoworker" as const, id: "LIST" }];
}

export const userCoworkerApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUserCoworkers: build.query<Paginated<UserCoworkerJSON>, UserCoworkerListArgs>({
      query: ({ query }) => ({
        url: "users/coworkers",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(UserCoworkerSchema.UserCoworkerJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: UserCoworkerJSON[], meta): Paginated<UserCoworkerJSON> => {
        return {
          data: response,
          headers: parseUserCoworkerPaginationHeaders(meta),
        };
      },
      providesTags: getUserCoworkerTags,
    }),
  }),
});

// The lazy hook is for callers that need the WHOLE list rather than a page someone is reading —
// "add all colleagues" walks the pages on one press. A component-level `useGetUserCoworkersQuery`
// cannot do that: the page index would have to be state, and each page would re-render the roster.
export const { useGetUserCoworkersQuery, useLazyGetUserCoworkersQuery } = userCoworkerApi;
